import * as t from 'io-ts';
import { head, isUndefined } from 'lodash-es';
import { isNotNullable } from '@bayer-int/portletce-shared';

import type {
    DrillDefinition,
    InteractiveTitleSelectDefinition,
    InteractiveTitleSettingDefinition,
    InteractiveTitleStaticStringDefinition,
} from 'header/types';
import { isDatabricksQuery } from 'portlet/models/query';

import { createSpecSettingsInterface, define } from './_common';

export interface DatabricksDrillSettingDefinition extends DrillDefinition {
    key: 'DATABRICKS_DRILL';
}

const definition: DatabricksDrillSettingDefinition = {
    key: 'DATABRICKS_DRILL',
    type: 'Drill',
    label: 'DatabricksDrill',
    allowMultiple: true,
    value: [],
    options: [],
};

const specSettingsInterface = createSpecSettingsInterface(definition, t.array(t.string));

export const codec = specSettingsInterface.input;

export const DatabricksDrillSettingDefinition = define({
    key: 'DATABRICKS_DRILL',
    label: 'dbxDrill',
    definition,
    fromSetting: (settings, spec, view) => {
        const drillValues = specSettingsInterface.extractValue(settings.dbxDrill);
        const query = view?.query || spec.query;

        if (!query || !isDatabricksQuery(query) || isUndefined(drillValues)) {
            return;
        }

        const drillableColumns = query.tables
            .flatMap(table => table.columns)
            .filter(col => col.drillable)
            .map(col => col.name);
        const validDrillValues = drillValues.filter(item => drillableColumns.includes(item));
        const value = query.drill?.allowMultiple ? validDrillValues : validDrillValues.slice(0, 1);

        return isUndefined(value)
            ? undefined
            : value.map((key, i) => ({
                  key,
                  order: i,
                  drill: key,
              }));
    },
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: (settings, spec, view) => {
        const query = view?.query || spec.query;

        if (isUndefined(settings.dbxDrill) || !query || !isDatabricksQuery(query)) {
            return;
        }

        const drillableColumnNames = query.tables
            .map(table => table.columns)
            .flat()
            .filter(col => col.drillable)
            .map(col => col.name);
        const defaultDrills = specSettingsInterface.extractValue(settings.dbxDrill);

        return {
            ...definition,
            ...specSettingsInterface.createDefinitionWithSettings(settings.dbxDrill),
            allowMultiple: !!query.drill?.allowMultiple,
            value:
                defaultDrills?.map((item, idx) => ({ key: item, drill: item, order: idx })) || [],
            options: drillableColumnNames.map(col => ({ key: col, label: col })),
        };
    },
    replacementTags: definition => {
        const dbxDrillValues = definition.value
            ? definition.value
                  .map(({ key }) => definition.options.find(opt => opt.key === key)?.label)
                  .filter(isNotNullable)
            : [];

        return {
            DBX_DRILL: definition.allowMultiple
                ? dbxDrillValues.join(', ')
                : dbxDrillValues[0] || '',
        };
    },

    interactiveTags: definition => {
        const notSupported: InteractiveTitleSettingDefinition<
            DatabricksDrillSettingDefinition,
            InteractiveTitleStaticStringDefinition
        > = {
            setting: definition,
            field: {
                type: 'StaticString',
                value: 'NOT SUPPORTED WITH MULTIPLE DRILLS',
            },
            format: () => definition,
        };

        const currentValue = definition.value && head(definition.value);
        const drillTag: InteractiveTitleSettingDefinition<
            DatabricksDrillSettingDefinition,
            InteractiveTitleSelectDefinition
        > = {
            setting: definition,
            field: {
                type: 'Select',
                value: currentValue ? currentValue.key : '',
                options: definition.options,
            },
            format: key => ({
                ...definition,
                value: key !== null ? [{ key, drill: key, order: 0 }] : null,
            }),
        };

        return {
            DBX_DRILL: definition.allowMultiple ? notSupported : drillTag,
        };
    },
});
